import { useQueryClient } from "@tanstack/react-query";
import { useDispatch, useSelector } from "react-redux";

import type { Signal, SignalType } from "app/pages/sales-intelligence/domain/signals/types/signal";
import managementTrackingService from "app/pages/sales-intelligence/modules/management/services/tracking/managementTrackingService";
import { resolveSignalsApiCustomAttributes } from "app/pages/sales-intelligence/modules/management/services/tracking/utils";
import { managementSignalsApiQueryKeys } from "app/pages/sales-intelligence/modules/management/sub-modules/signals-api/query/key-factory/queryKeyFactory";
import { useSignalsApiStatusPollingQuery } from "app/pages/sales-intelligence/modules/management/sub-modules/signals-api/query/queries/useSignalsApiStatusPollingQuery";
import { signalsRestApiQueryKeys } from "app/pages/sales-intelligence/modules/signals-rest-api/query/key-factory/queryKeyFactory";
import siNavigator from "app/pages/sales-intelligence/services/siNavigator";
import SignalSource from "app/pages/sales-intelligence/sub-modules/signals/models/core/SignalSource";
import {
    isSignalsManagementOpenAction,
    setInitialSelectedSignalType,
    setManagementDeleteSignalId,
    setManagementEditSignalId,
    setPreselectedSignalType,
    setSignalIdForHighlight,
} from "app/pages/sales-intelligence/sub-modules/signals/store/action-creators";
import { fetchSignalsThunk } from "app/pages/sales-intelligence/sub-modules/signals/store/effects";
import {
    selectInitialSelectedSignalType,
    selectIsSignalsManagementOpen,
    selectNumberOfSignalsFromSource,
    selectPreselectedSignalType,
    selectSignalsSource,
} from "app/pages/sales-intelligence/sub-modules/signals/store/selectors";

import BaseSignalsManagementContainer from "../BaseSignalsManagementContainer/BaseSignalsManagementContainer";

const SignalsManagementCommonContainer = () => {
    const dispatch = useDispatch();
    const queryClient = useQueryClient();

    const numberOfSignals = useSelector(selectNumberOfSignalsFromSource);
    const isSignalsManagementOpen = useSelector(selectIsSignalsManagementOpen);
    const preselectedSignalType = useSelector(selectPreselectedSignalType);
    const initialSelectedType = useSelector(selectInitialSelectedSignalType);
    const signalSource = useSelector(selectSignalsSource);
    const { data } = useSignalsApiStatusPollingQuery();

    function handleClose(): void {
        dispatch(isSignalsManagementOpenAction(false));

        dispatch(setPreselectedSignalType(null));
        dispatch(setInitialSelectedSignalType(null));
        dispatch(setManagementEditSignalId(null));
        dispatch(setManagementDeleteSignalId(null));
    }

    function handleUpdateSignalEvents(signal?: Signal) {
        if (signalSource === SignalSource.Api || signalSource === SignalSource.RestApi) {
            const signalTrackingParams = signal ? `${signal.signalType}/` : "";
            managementTrackingService.trackSignalApiClickEvent(
                `added successfully/${signalTrackingParams}`,
                resolveSignalsApiCustomAttributes(data),
            );
            queryClient.invalidateQueries({ queryKey: managementSignalsApiQueryKeys.getStatus() });
        }

        if (signalSource === SignalSource.RestApi) {
            queryClient.invalidateQueries({ queryKey: signalsRestApiQueryKeys.getConfiguredStatus() });
        }

        void dispatch(fetchSignalsThunk());
    }

    function handleSignalCreateSuccess(signal: Signal): void {
        handleUpdateSignalEvents(signal);

        if (siNavigator.isAccountSignalsManagementPage) {
            dispatch(setSignalIdForHighlight(signal.id));
        }
    }

    function handleManagementViewToggle() {
        if (isSignalsManagementOpen) {
            handleClose();

            return;
        }

        dispatch(isSignalsManagementOpenAction(true));
    }

    function handleSignalCreate(signalType: SignalType | null): void {
        dispatch(setPreselectedSignalType(signalType));
    }

    function handleDeleteViewClose() {
        if (numberOfSignals > 1) {
            return;
        }

        handleUpdateSignalEvents();
    }

    return (
        <BaseSignalsManagementContainer
            isManagementViewOpen={isSignalsManagementOpen}
            initialSelectedType={initialSelectedType ?? undefined}
            selectedSignalCreateType={preselectedSignalType}
            onManagementViewToggle={handleManagementViewToggle}
            onManagementViewClose={handleClose}
            onSignalCreate={handleSignalCreate}
            onDeleteViewClose={handleDeleteViewClose}
            onSignalCreateSuccess={handleSignalCreateSuccess}
            onSignalUpdateSuccess={handleUpdateSignalEvents}
        />
    );
};

export default SignalsManagementCommonContainer;
